import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import i18n from '../i18n';
import { BrandTheme, BrandThemeV2 } from '../types/theme.v2';
import { BrandThemeV3 } from '../types/theme.v3';
import { DEFAULT_THEME } from '../utils/defaultTheme';
import { getBrandTheme } from '../utils/brandThemes';
import { TenantInfo } from '../services/hotel.service';
import { createThemeFromTenantInfo } from '../utils/themeConverter';
import { applyThemeToVariables } from '../utils/themeLoader';
import { flattenLocaleMapsShallow } from '../utils/localeMap';
import { Sentry } from '../utils/sentry';

interface ThemeContextType {
  theme: BrandTheme;
  tenantData: TenantInfo | null;
  isLoading: boolean;
  error: string | null;
  loadTheme: (tenantSlug: string) => Promise<void>;
  setThemeFromTenantInfo: (tenantInfo: TenantInfo) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error(i18n.t('contexts.themeContext.useThemeOutsideProvider'));
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
  tenantSlug?: string;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, tenantSlug }) => {
  const [theme, setTheme] = useState<BrandTheme>(DEFAULT_THEME);
  const [tenantData, setTenantData] = useState<TenantInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper function to convert hex to RGB triplet for rgba() usage
  const hexToRgb = (hex: string): string => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return '0, 0, 0';
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `${r}, ${g}, ${b}`;
  };

  const applyCSSVariables = (theme: BrandTheme) => {
    const root = document.documentElement;
    console.log('🎨 Applying theme from API:', theme.brand.name);
    console.log('🎨 Full theme object received:', JSON.stringify(theme, null, 2));

    // ============================================================
    // PRIMITIVE CSS VARIABLES (for theme-components/*.css files)
    // Maps API data to variables used by primitive components
    // ============================================================

    // ── Fonts ──
    root.style.setProperty('--font-display', theme.brand.fonts.primary);
    root.style.setProperty('--font-body', theme.brand.fonts.secondary || theme.brand.fonts.primary);
    root.style.setProperty('--font-mono', 'ui-monospace, "SF Mono", "Cascadia Mono", "Courier New", monospace');

    // ── Brand Colors (with RGB variants) ──
    root.style.setProperty('--color-primary', theme.brand.colors.primary);
    root.style.setProperty('--color-primary-rgb', hexToRgb(theme.brand.colors.primary));
    root.style.setProperty('--color-secondary', theme.brand.colors.secondary);
    root.style.setProperty('--color-secondary-rgb', hexToRgb(theme.brand.colors.secondary));
    root.style.setProperty('--color-gold', theme.brand.colors.cta);
    root.style.setProperty('--color-gold-text', theme.brand.colors.ctaText);
    root.style.setProperty('--color-accent', theme.brand.colors.cta);
    root.style.setProperty('--color-accent-rgb', hexToRgb(theme.brand.colors.cta));

    // ── Text Colors (with RGB variants) ──
    root.style.setProperty('--color-text-primary', theme.brand.colors.text.primary);
    root.style.setProperty('--color-text-primary-rgb', hexToRgb(theme.brand.colors.text.primary));
    root.style.setProperty('--color-text-secondary', theme.brand.colors.text.secondary);
    root.style.setProperty('--color-text-secondary-rgb', hexToRgb(theme.brand.colors.text.secondary));
    root.style.setProperty('--color-text-light', theme.brand.colors.text.light);
    root.style.setProperty('--color-text-disabled', theme.brand.colors.text.disabled);
    root.style.setProperty('--color-text-muted', theme.brand.colors.text.disabled);

    // ── Surface Colors (with RGB variants) ──
    if (theme.brand.colors.surface) {
      root.style.setProperty('--color-surface-base', theme.brand.colors.surface.base);
      root.style.setProperty('--color-surface-base-rgb', hexToRgb(theme.brand.colors.surface.base));
      root.style.setProperty('--color-surface-subtle', theme.brand.colors.surface.subtle);
      root.style.setProperty('--color-surface-warm', theme.brand.colors.surface.warm);
      root.style.setProperty('--color-surface-hover', theme.brand.colors.surface.hover);
      root.style.setProperty('--color-border', theme.brand.colors.surface.border);
      root.style.setProperty('--color-border-subtle', theme.brand.colors.surface.borderSubtle);
    }

    // ── Shadows ──
    root.style.setProperty('--shadow-card', theme.brand.shadows.light);
    root.style.setProperty('--shadow-card-medium', theme.brand.shadows.medium);
    root.style.setProperty('--shadow-modal', '0 20px 60px rgba(0, 0, 0, 0.3)');

    // ── Status Colors (with RGB variants) ──
    // Using default values since BrandTheme doesn't have status property yet
    root.style.setProperty('--color-error', '#DC2626');
    root.style.setProperty('--color-error-rgb', hexToRgb('#DC2626'));
    root.style.setProperty('--color-error-bg', '#FEF2F2');
    root.style.setProperty('--color-success', '#22C55E');
    root.style.setProperty('--color-success-rgb', hexToRgb('#22C55E'));
    root.style.setProperty('--color-success-bg', '#F0FDF4');
    root.style.setProperty('--color-warning', '#EA580C');
    root.style.setProperty('--color-warning-rgb', hexToRgb('#EA580C'));
    root.style.setProperty('--color-warning-bg', '#FFF7ED');
    root.style.setProperty('--color-warning-text', theme.brand.colors.text.primary);

    // ── TransactionalUI - Buttons ──
    if (theme.transactionalUI?.button) {
      // Primary Button
      if (theme.transactionalUI.button.primary) {
        root.style.setProperty('--button-primary-bg', theme.transactionalUI.button.primary.background);
        root.style.setProperty('--button-primary-text', theme.transactionalUI.button.primary.text);
        root.style.setProperty('--button-primary-hover-bg', theme.transactionalUI.button.primary.hoverBackground);
        root.style.setProperty('--button-primary-padding', theme.transactionalUI.button.primary.padding);
        root.style.setProperty('--color-primary-hover', theme.transactionalUI.button.primary.hoverBackground);
        root.style.setProperty('--color-on-primary', theme.transactionalUI.button.primary.text);
      }

      // Secondary Button (Outlined)
      if (theme.transactionalUI.button.secondary) {
        root.style.setProperty('--button-secondary-bg', theme.transactionalUI.button.secondary.background);
        root.style.setProperty('--button-secondary-text', theme.transactionalUI.button.secondary.text);
        root.style.setProperty('--button-secondary-hover-bg', theme.transactionalUI.button.secondary.hoverBackground);
      }

      // CTA Button (Gold)
      if (theme.transactionalUI.button.cta) {
        root.style.setProperty('--button-cta-bg', theme.transactionalUI.button.cta.background);
        root.style.setProperty('--button-cta-text', theme.transactionalUI.button.cta.text);
        root.style.setProperty('--button-cta-hover-bg', theme.transactionalUI.button.cta.hoverBackground);
        root.style.setProperty('--color-gold-hover', theme.transactionalUI.button.cta.hoverBackground);
        root.style.setProperty('--color-accent-hover', theme.transactionalUI.button.cta.hoverBackground);
        root.style.setProperty('--color-on-gold', theme.transactionalUI.button.cta.text);
        root.style.setProperty('--color-on-accent', theme.transactionalUI.button.cta.text);
      }
    }

    // ── TransactionalUI - Card ──
    if (theme.transactionalUI?.card) {
      root.style.setProperty('--card-padding', theme.transactionalUI.card.padding);
      root.style.setProperty('--card-bg', theme.transactionalUI.card.background);
      root.style.setProperty('--card-border', theme.transactionalUI.card.border);
      root.style.setProperty('--card-shadow', theme.transactionalUI.card.shadow);
      if (theme.transactionalUI.card.cornerAccent) {
        console.log('🎨 [ThemeContext] Setting corner accent (v3):', {
          color: theme.transactionalUI.card.cornerAccent.color,
          enabled: theme.transactionalUI.card.cornerAccent.enabled,
          display: theme.transactionalUI.card.cornerAccent.enabled ? 'block' : 'none'
        });
        root.style.setProperty('--card-corner-accent', theme.transactionalUI.card.cornerAccent.color);
        // Set display based on enabled flag: 'block' shows the accent, 'none' hides it
        root.style.setProperty('--card-corner-accent-display', theme.transactionalUI.card.cornerAccent.enabled ? 'block' : 'none');

        // Verify the value was set correctly
        const verifyColor = getComputedStyle(document.documentElement).getPropertyValue('--card-corner-accent');
        console.log('✅ [ThemeContext] Verified CSS variable:', {
          '--card-corner-accent': verifyColor.trim() || '(empty)',
          expected: theme.transactionalUI.card.cornerAccent.color
        });
      }
    }

    // ── TransactionalUI - Section Heading ──
    if (theme.transactionalUI?.sectionHeading) {
      root.style.setProperty('--section-heading-font', theme.transactionalUI.sectionHeading.fontFamily);
      root.style.setProperty('--section-heading-size', theme.transactionalUI.sectionHeading.fontSize);
      root.style.setProperty('--section-heading-weight', theme.transactionalUI.sectionHeading.fontWeight);
      root.style.setProperty('--section-heading-color', theme.transactionalUI.sectionHeading.color);
      root.style.setProperty('--section-heading-spacing', theme.transactionalUI.sectionHeading.letterSpacing);

      // Decorative Rule
      if (theme.transactionalUI.sectionHeading.decorativeRule) {
        const rule = theme.transactionalUI.sectionHeading.decorativeRule;
        console.log('🎨 [ThemeContext] Setting decorative rule:', {
          enabled: rule.enabled,
          lineColor: rule.lineColor,
          diamondColor: rule.diamondColor
        });
        root.style.setProperty('--decorative-rule-display', rule.enabled ? 'flex' : 'none');
        root.style.setProperty('--decorative-rule-line-width', rule.lineWidth);
        root.style.setProperty('--decorative-rule-line-color', rule.lineColor);
        root.style.setProperty('--decorative-rule-diamond-size', rule.diamondSize);
        root.style.setProperty('--decorative-rule-diamond-color', rule.diamondColor);
      }
    }

    // ── TransactionalUI - Progress Stepper ──
    // Using brand.progressStepper from v2 theme structure
    if (theme.brand?.progressStepper) {
      const stepper = theme.brand.progressStepper;
      root.style.setProperty('--stepper-label-font-size', stepper.label.fontSize);
      root.style.setProperty('--stepper-label-font', stepper.label.font);
      root.style.setProperty('--stepper-label-font-weight', stepper.label.fontWeight);
      root.style.setProperty('--stepper-label-color', stepper.label.color);
      root.style.setProperty('--stepper-label-active-color', stepper.label.activeColor);
      root.style.setProperty('--stepper-completed-bg', stepper.completed.background);
      root.style.setProperty('--stepper-completed-text', stepper.completed.text);
      root.style.setProperty('--stepper-completed-border', stepper.completed.border);
      root.style.setProperty('--stepper-completed-check', stepper.completed.check);
      root.style.setProperty('--stepper-active-bg', stepper.active.background);
      root.style.setProperty('--stepper-active-text', stepper.active.text);
      root.style.setProperty('--stepper-active-border', stepper.active.border);
      root.style.setProperty('--stepper-pending-bg', stepper.pending.background);
      root.style.setProperty('--stepper-pending-text', stepper.pending.text);
      root.style.setProperty('--stepper-pending-border', stepper.pending.border);
      root.style.setProperty('--stepper-connector-color', stepper.connector.color);
      root.style.setProperty('--stepper-connector-completed', stepper.connector.completed);
      root.style.setProperty('--stepper-connector-width', stepper.connector.width);
    }

    // ── Focus States (Accessibility) ──
    if (theme.accessibility) {
      root.style.setProperty('--focus-border', theme.accessibility.focusOutline);
      root.style.setProperty('--focus-ring', `0 0 0 ${theme.accessibility.focusOutlineWidth} rgba(${hexToRgb(theme.accessibility.focusOutline)}, 0.15)`);
      root.style.setProperty('--color-focus-shadow', `rgba(${hexToRgb(theme.accessibility.focusOutline)}, 0.15)`);
    }

    // ── Spacing ──
    if (theme.transactionalUI) {
      root.style.setProperty('--btn-padding', theme.transactionalUI.button?.primary?.padding || '14px 32px');
      root.style.setProperty('--input-padding', '14px 16px');
    }

    // ── Overlays ──
    root.style.setProperty('--color-overlay-dark', 'rgba(0, 0, 0, 0.8)');
    root.style.setProperty('--color-surface-dark', '#000000');
    root.style.setProperty('--color-surface-dark-rgb', '0, 0, 0');
    root.style.setProperty('--color-on-dark', '#FFFFFF');

    // ============================================================
    // LEGACY VARIABLES (for backwards compatibility with old code)
    // ============================================================
    root.style.setProperty('--color-input-active-bg', theme.userInterface.inputBox.active.background);
    root.style.setProperty('--color-input-active-border', theme.userInterface.inputBox.active.border);
    root.style.setProperty('--color-send-button-bg', theme.userInterface.sendButton.background);
    root.style.setProperty('--font-primary', theme.brand.fonts.primary);
    root.style.setProperty('--font-secondary', 'Georgia, serif');
    
    // Legacy brand colors (keeping for compatibility)
    root.style.setProperty('--brand-primary', theme.brand.colors.primary);
    root.style.setProperty('--brand-secondary', theme.brand.colors.secondary);
    root.style.setProperty('--brand-cta', theme.brand.colors.cta);
    root.style.setProperty('--accent-gold', theme.brand.colors.cta);
    root.style.setProperty('--brand-gradient-start', theme.brand.colors.gradient.start);
    root.style.setProperty('--brand-gradient-middle', theme.brand.colors.gradient.middle);
    root.style.setProperty('--brand-gradient-end', theme.brand.colors.gradient.end);
    root.style.setProperty('--brand-text-primary', theme.brand.colors.text.primary);
    root.style.setProperty('--brand-text-secondary', theme.brand.colors.text.secondary);
    root.style.setProperty('--brand-text-light', theme.brand.colors.text.light);

    // Backward compatibility - optional properties
    if (theme.brand.colors.accent) {
      root.style.setProperty('--brand-accent', theme.brand.colors.accent);
    }
    if (theme.brand.colors.inactive) {
      root.style.setProperty('--brand-inactive', theme.brand.colors.inactive);
    }
    if (theme.brand.colors.border) {
      root.style.setProperty('--brand-border', theme.brand.colors.border);
    }
    if (theme.brand.colors.text.placeholder) {
      root.style.setProperty('--brand-text-placeholder', theme.brand.colors.text.placeholder);
    }

    // Typography
    root.style.setProperty('--brand-font-primary', theme.brand.fonts.primary);

    // Shadows
    root.style.setProperty('--brand-shadow-light', theme.brand.shadows.light);
    root.style.setProperty('--brand-shadow-medium', theme.brand.shadows.medium);

    // UI Components
    root.style.setProperty('--ui-topbar-bg', theme.userInterface.topBar.background);
    root.style.setProperty('--ui-topbar-text', theme.userInterface.topBar.text);
    root.style.setProperty('--ui-topbar-border', theme.userInterface.topBar.borderBottom);

    root.style.setProperty('--ui-user-msg-bg', theme.userInterface.userMessage.background);
    root.style.setProperty('--ui-user-msg-text', theme.userInterface.userMessage.text);
    root.style.setProperty('--ui-user-msg-border', theme.userInterface.userMessage.border);
    root.style.setProperty('--ui-user-msg-shadow', theme.userInterface.userMessage.shadow);

    root.style.setProperty('--ui-input-inactive-bg', theme.userInterface.inputBox.inactive.background);
    root.style.setProperty('--ui-input-inactive-text', theme.userInterface.inputBox.inactive.text);
    root.style.setProperty('--ui-input-inactive-border', theme.userInterface.inputBox.inactive.border);
    root.style.setProperty('--ui-input-inactive-placeholder', theme.userInterface.inputBox.inactive.placeholder);

    root.style.setProperty('--ui-input-active-bg', theme.userInterface.inputBox.active.background);
    root.style.setProperty('--ui-input-active-text', theme.userInterface.inputBox.active.text);
    root.style.setProperty('--ui-input-active-border', theme.userInterface.inputBox.active.border);
    root.style.setProperty('--ui-input-active-shadow', theme.userInterface.inputBox.active.shadow);

    root.style.setProperty('--ui-send-btn-bg', theme.userInterface.sendButton.background);
    root.style.setProperty('--ui-send-btn-text', theme.userInterface.sendButton.text);
    root.style.setProperty('--ui-send-btn-border', theme.userInterface.sendButton.border);
    root.style.setProperty('--ui-send-btn-shadow', theme.userInterface.sendButton.shadow);
    root.style.setProperty('--ui-send-btn-hover-bg', theme.userInterface.sendButton.hover.background);
    root.style.setProperty('--ui-send-btn-hover-text', theme.userInterface.sendButton.hover.text);
    root.style.setProperty('--ui-send-btn-hover-border', theme.userInterface.sendButton.hover.border);
    root.style.setProperty('--ui-send-btn-hover-shadow', theme.userInterface.sendButton.hover.shadow);
    root.style.setProperty('--ui-send-btn-disabled-bg', theme.userInterface.sendButton.disabled.background);
    root.style.setProperty('--ui-send-btn-disabled-border', theme.userInterface.sendButton.disabled.border);

    // AI Response System
    if (theme.aiResponseSystem?.responseCard) {
      root.style.setProperty('--ai-card-bg', theme.aiResponseSystem.responseCard.background);
      root.style.setProperty('--ai-card-border', theme.aiResponseSystem.responseCard.border);
      root.style.setProperty('--ai-card-shadow', theme.aiResponseSystem.responseCard.shadow);
      root.style.setProperty('--ai-card-radius', theme.aiResponseSystem.responseCard.borderRadius);
    }

    if (theme.aiResponseSystem?.avatar) {
      root.style.setProperty('--ai-avatar-size', theme.aiResponseSystem.avatar.size);
      root.style.setProperty('--ai-avatar-radius', theme.aiResponseSystem.avatar.borderRadius);
      root.style.setProperty('--ai-avatar-shadow', theme.aiResponseSystem.avatar.shadow);
      root.style.setProperty('--ai-avatar-border', theme.aiResponseSystem.avatar.border);
    }

    // Content types
    const contentTypes = theme.aiResponseSystem?.contentTypes;
    if (contentTypes?.text) {
      root.style.setProperty('--ai-text-color', contentTypes.text.color);
      root.style.setProperty('--ai-text-size', contentTypes.text.fontSize);
      root.style.setProperty('--ai-text-height', contentTypes.text.lineHeight);
    }

    if (contentTypes?.highlight) {
      root.style.setProperty('--ai-highlight-bg', contentTypes.highlight.background);
      root.style.setProperty('--ai-highlight-border', contentTypes.highlight.border);
      root.style.setProperty('--ai-highlight-border-left', contentTypes.highlight.borderLeft);
      root.style.setProperty('--ai-highlight-text', contentTypes.highlight.text);
    }

    // Suggestion Card (v3.5.1+)
    if ((contentTypes as any)?.suggestionCard) {
      const sc = (contentTypes as any).suggestionCard as any;
      root.style.setProperty('--ai-suggestion-bg', sc.background);
      root.style.setProperty('--ai-suggestion-border', sc.border);
      root.style.setProperty('--ai-suggestion-padding', sc.padding);
      root.style.setProperty('--ai-suggestion-gap', sc.gap);
      root.style.setProperty('--ai-suggestion-min-height', sc.minHeight);

      if (sc.accentBar) {
        root.style.setProperty('--ai-suggestion-accent-width', sc.accentBar.width);
        root.style.setProperty('--ai-suggestion-accent-color', sc.accentBar.color);
        root.style.setProperty('--ai-suggestion-accent-hover-width', sc.accentBar.hoverWidth);
      }

      if (sc.hover) {
        root.style.setProperty('--ai-suggestion-hover-bg', sc.hover.background);
        root.style.setProperty('--ai-suggestion-hover-border', sc.hover.border);
        root.style.setProperty('--ai-suggestion-hover-shadow', sc.hover.shadow);
      }

      if (sc.focus) {
        root.style.setProperty('--ai-suggestion-focus-outline', sc.focus.outline);
        root.style.setProperty('--ai-suggestion-focus-offset', sc.focus.outlineOffset);
      }

      if (sc.iconRing) {
        root.style.setProperty('--ai-suggestion-icon-size', sc.iconRing.size);
        root.style.setProperty('--ai-suggestion-icon-bg', sc.iconRing.background);
        root.style.setProperty('--ai-suggestion-icon-border', sc.iconRing.border);
        root.style.setProperty('--ai-suggestion-icon-color', sc.iconRing.iconColor);
        root.style.setProperty('--ai-suggestion-icon-hover-color', sc.iconRing.hoverIconColor);
        root.style.setProperty('--ai-suggestion-icon-hover-border', sc.iconRing.hoverBorderColor);
      }

      if (sc.actionLabel) {
        root.style.setProperty('--ai-suggestion-action-font', sc.actionLabel.fontFamily);
        root.style.setProperty('--ai-suggestion-action-size', sc.actionLabel.fontSize);
        root.style.setProperty('--ai-suggestion-action-weight', sc.actionLabel.fontWeight);
        root.style.setProperty('--ai-suggestion-action-color', sc.actionLabel.color);
      }

      if (sc.hintLabel) {
        root.style.setProperty('--ai-suggestion-hint-font', sc.hintLabel.fontFamily);
        root.style.setProperty('--ai-suggestion-hint-size', sc.hintLabel.fontSize);
        root.style.setProperty('--ai-suggestion-hint-color', sc.hintLabel.color);
      }

      if (sc.sectionLabel) {
        root.style.setProperty('--ai-suggestion-section-font', sc.sectionLabel.fontFamily);
        root.style.setProperty('--ai-suggestion-section-size', sc.sectionLabel.fontSize);
        root.style.setProperty('--ai-suggestion-section-weight', sc.sectionLabel.fontWeight);
        root.style.setProperty('--ai-suggestion-section-color', sc.sectionLabel.color);
        root.style.setProperty('--ai-suggestion-section-transform', sc.sectionLabel.textTransform);
        root.style.setProperty('--ai-suggestion-section-spacing', sc.sectionLabel.letterSpacing);
      }
    }

    // Video content
    if (contentTypes?.video) {
      root.style.setProperty('--ai-video-bg', contentTypes.video.background);
      root.style.setProperty('--ai-video-border', contentTypes.video.border);
      root.style.setProperty('--ai-video-radius', contentTypes.video.borderRadius);
      root.style.setProperty('--ai-video-shadow', contentTypes.video.shadow);
      if (contentTypes.video.controls) {
        root.style.setProperty('--ai-video-controls-bg', contentTypes.video.controls.background);
        root.style.setProperty('--ai-video-controls-color', contentTypes.video.controls.color);
      }
      if (contentTypes.video.caption) {
        root.style.setProperty('--ai-video-caption-bg', contentTypes.video.caption.background);
        root.style.setProperty('--ai-video-caption-text', contentTypes.video.caption.text);
        root.style.setProperty('--ai-video-caption-size', contentTypes.video.caption.fontSize);
        root.style.setProperty('--ai-video-caption-padding', contentTypes.video.caption.padding);
        root.style.setProperty('--ai-video-caption-border', contentTypes.video.caption.borderTop);
      }
    }

    // AI Assistant
    if (theme.aiAssistant?.avatar?.gradient) {
      root.style.setProperty('--ai-assistant-gradient-start', theme.aiAssistant.avatar.gradient.start);
      root.style.setProperty('--ai-assistant-gradient-end', theme.aiAssistant.avatar.gradient.end);
    }
    if (theme.aiAssistant?.avatar?.fallback) {
      root.style.setProperty('--ai-assistant-fallback-bg', theme.aiAssistant.avatar.fallback.backgroundColor);
    }
    if (theme.aiAssistant?.nameStyle) {
      root.style.setProperty('--ai-assistant-name-color', theme.aiAssistant.nameStyle.color);
      root.style.setProperty('--ai-assistant-name-weight', theme.aiAssistant.nameStyle.fontWeight);
      root.style.setProperty('--ai-assistant-name-size', theme.aiAssistant.nameStyle.fontSize);
    }
    if (theme.aiAssistant?.timestamp) {
      root.style.setProperty('--ai-assistant-time-color', theme.aiAssistant.timestamp.color);
      root.style.setProperty('--ai-assistant-time-size', theme.aiAssistant.timestamp.fontSize);
    }

    // Accessibility
    if (theme.accessibility) {
      root.style.setProperty('--a11y-focus-outline', theme.accessibility.focusOutline);
      root.style.setProperty('--a11y-focus-width', theme.accessibility.focusOutlineWidth);
      root.style.setProperty('--a11y-focus-offset', theme.accessibility.focusOutlineOffset);
      root.style.setProperty('--a11y-min-touch', theme.accessibility.minTouchTarget);
    }

    // ===== NEW THEME V2.0 VARIABLES =====

    // Surface colors
    if (theme.brand.colors.surface) {
      root.style.setProperty('--surface-base', theme.brand.colors.surface.base);
      root.style.setProperty('--surface-subtle', theme.brand.colors.surface.subtle);
      root.style.setProperty('--surface-warm', theme.brand.colors.surface.warm);
      root.style.setProperty('--surface-hover', theme.brand.colors.surface.hover);
      root.style.setProperty('--surface-border', theme.brand.colors.surface.border);
      root.style.setProperty('--surface-border-subtle', theme.brand.colors.surface.borderSubtle);
    }

    // Extended text colors
    if (theme.brand.colors.text.disabled) {
      root.style.setProperty('--text-disabled', theme.brand.colors.text.disabled);
    }
    if (theme.brand.colors.text.placeholder) {
      root.style.setProperty('--text-placeholder', theme.brand.colors.text.placeholder);
    }

    // Extended shadows
    if (theme.brand.shadows.aiCard) {
      root.style.setProperty('--shadow-ai-card', theme.brand.shadows.aiCard);
    }
    if (theme.brand.shadows.focus) {
      root.style.setProperty('--shadow-focus', theme.brand.shadows.focus);
    }

    // Buttons
    if (theme.brand.buttons) {
      // Primary button
      root.style.setProperty('--button-primary-text', theme.brand.buttons.primary.text);
      root.style.setProperty('--button-primary-bg', theme.brand.buttons.primary.background);
      root.style.setProperty('--button-primary-hover-bg', theme.brand.buttons.primary.hoverBackground);
      root.style.setProperty('--button-primary-border', theme.brand.buttons.primary.border);
      root.style.setProperty('--button-primary-padding', theme.brand.buttons.primary.padding);
      root.style.setProperty('--button-primary-font-size', theme.brand.buttons.primary.fontSize);
      root.style.setProperty('--button-primary-font-weight', theme.brand.buttons.primary.fontWeight);
      root.style.setProperty('--button-primary-border-radius', theme.brand.buttons.primary.borderRadius);
      root.style.setProperty('--button-primary-letter-spacing', theme.brand.buttons.primary.letterSpacing);

      // Secondary button
      root.style.setProperty('--button-secondary-text', theme.brand.buttons.secondary.text);
      root.style.setProperty('--button-secondary-bg', theme.brand.buttons.secondary.background);
      root.style.setProperty('--button-secondary-hover-bg', theme.brand.buttons.secondary.hoverBackground);
      root.style.setProperty('--button-secondary-border', theme.brand.buttons.secondary.border);
      root.style.setProperty('--button-secondary-padding', theme.brand.buttons.secondary.padding);
      root.style.setProperty('--button-secondary-font-size', theme.brand.buttons.secondary.fontSize);
      root.style.setProperty('--button-secondary-font-weight', theme.brand.buttons.secondary.fontWeight);
      root.style.setProperty('--button-secondary-border-radius', theme.brand.buttons.secondary.borderRadius);
      root.style.setProperty('--button-secondary-letter-spacing', theme.brand.buttons.secondary.letterSpacing);

      // CTA button
      root.style.setProperty('--button-cta-text', theme.brand.buttons.cta.text);
      root.style.setProperty('--button-cta-bg', theme.brand.buttons.cta.background);
      root.style.setProperty('--button-cta-hover-bg', theme.brand.buttons.cta.hoverBackground);
      root.style.setProperty('--button-cta-border', theme.brand.buttons.cta.border);
      root.style.setProperty('--button-cta-padding', theme.brand.buttons.cta.padding);
      root.style.setProperty('--button-cta-font-size', theme.brand.buttons.cta.fontSize);
      root.style.setProperty('--button-cta-font-weight', theme.brand.buttons.cta.fontWeight);
      root.style.setProperty('--button-cta-border-radius', theme.brand.buttons.cta.borderRadius);
      if (theme.brand.buttons.cta.minFontSize) {
        root.style.setProperty('--button-cta-min-font-size', theme.brand.buttons.cta.minFontSize);
      }
    }

    // Chips
    if (theme.brand.chips) {
      root.style.setProperty('--chip-text', theme.brand.chips.text);
      root.style.setProperty('--chip-bg', theme.brand.chips.background);
      root.style.setProperty('--chip-hover-text', theme.brand.chips.hoverText);
      root.style.setProperty('--chip-hover-bg', theme.brand.chips.hoverBackground);
      root.style.setProperty('--chip-hover-border', theme.brand.chips.hoverBorder);
      root.style.setProperty('--chip-border', theme.brand.chips.border);
      root.style.setProperty('--chip-padding', theme.brand.chips.padding);
      root.style.setProperty('--chip-font-size', theme.brand.chips.fontSize);
      root.style.setProperty('--chip-min-height', theme.brand.chips.minHeight);
      root.style.setProperty('--chip-border-radius', theme.brand.chips.borderRadius);
    }

    // Form Inputs
    if (theme.brand.formInputs) {
      root.style.setProperty('--input-border', theme.brand.formInputs.border);
      root.style.setProperty('--input-bg', theme.brand.formInputs.background);
      root.style.setProperty('--input-focus-border', theme.brand.formInputs.focusBorder);
      root.style.setProperty('--input-focus-shadow', theme.brand.formInputs.focusShadow);
      root.style.setProperty('--input-text', theme.brand.formInputs.text);
      root.style.setProperty('--input-placeholder', theme.brand.formInputs.placeholder);
      root.style.setProperty('--input-font-size', theme.brand.formInputs.fontSize);
      root.style.setProperty('--input-padding', theme.brand.formInputs.padding);
      root.style.setProperty('--input-border-radius', theme.brand.formInputs.borderRadius);
    }

    // Calendar
    if (theme.brand.calendar) {
      root.style.setProperty('--calendar-bg', theme.brand.calendar.background);
      root.style.setProperty('--calendar-border', theme.brand.calendar.border);
      root.style.setProperty('--calendar-border-radius', theme.brand.calendar.borderRadius);
      root.style.setProperty('--calendar-padding', theme.brand.calendar.padding);
      root.style.setProperty('--calendar-shadow', theme.brand.calendar.shadow);
      root.style.setProperty('--calendar-month-font', theme.brand.calendar.month.font);
      root.style.setProperty('--calendar-month-font-size', theme.brand.calendar.month.fontSize);
      root.style.setProperty('--calendar-month-font-weight', theme.brand.calendar.month.fontWeight);
      root.style.setProperty('--calendar-month-color', theme.brand.calendar.month.color);
      root.style.setProperty('--calendar-day-selected-color', theme.brand.calendar.daySelected.color);
      root.style.setProperty('--calendar-day-selected-bg', theme.brand.calendar.daySelected.background);
      root.style.setProperty('--calendar-day-selected-border', theme.brand.calendar.daySelected.border);
    }

    // Pricing
    if (theme.brand.pricing) {
      root.style.setProperty('--pricing-amount-font', theme.brand.pricing.amount.font);
      root.style.setProperty('--pricing-amount-font-size', theme.brand.pricing.amount.fontSize);
      root.style.setProperty('--pricing-amount-font-weight', theme.brand.pricing.amount.fontWeight);
      root.style.setProperty('--pricing-amount-color', theme.brand.pricing.amount.color);
      root.style.setProperty('--pricing-currency-font-size', theme.brand.pricing.currency.fontSize);
      root.style.setProperty('--pricing-currency-font-weight', theme.brand.pricing.currency.fontWeight);
      root.style.setProperty('--pricing-currency-color', theme.brand.pricing.currency.color);
      root.style.setProperty('--pricing-total-font-size', theme.brand.pricing.total.fontSize);
      root.style.setProperty('--pricing-total-font-weight', theme.brand.pricing.total.fontWeight);
      root.style.setProperty('--pricing-total-color', theme.brand.pricing.total.color);
      root.style.setProperty('--pricing-strikethrough-color', theme.brand.pricing.strikethrough.color);
      root.style.setProperty('--pricing-savings-color', theme.brand.pricing.savings.color);
      root.style.setProperty('--pricing-savings-bg', theme.brand.pricing.savings.background);
      root.style.setProperty('--pricing-savings-font-size', theme.brand.pricing.savings.fontSize);
      root.style.setProperty('--pricing-savings-font-weight', theme.brand.pricing.savings.fontWeight);
    }

    // Badges
    if (theme.brand.badges) {
      root.style.setProperty('--badge-text', theme.brand.badges.text);
      root.style.setProperty('--badge-bg', theme.brand.badges.background);
      root.style.setProperty('--badge-border', theme.brand.badges.border);
      root.style.setProperty('--badge-font-size', theme.brand.badges.fontSize);
      root.style.setProperty('--badge-font-weight', theme.brand.badges.fontWeight);
      root.style.setProperty('--badge-letter-spacing', theme.brand.badges.letterSpacing);
      root.style.setProperty('--badge-text-transform', theme.brand.badges.textTransform);
      root.style.setProperty('--badge-padding', theme.brand.badges.padding);
    }

    // Reservation Card
    if (theme.brand.reservationCard) {
      root.style.setProperty('--reservation-image-border-radius', theme.brand.reservationCard.imageBorderRadius);
      root.style.setProperty('--reservation-image-overlay', theme.brand.reservationCard.imageOverlay);
      root.style.setProperty('--reservation-badge-text', theme.brand.reservationCard.badge.text);
      root.style.setProperty('--reservation-badge-bg', theme.brand.reservationCard.badge.background);
      root.style.setProperty('--reservation-badge-font-size', theme.brand.reservationCard.badge.fontSize);
      root.style.setProperty('--reservation-badge-font-weight', theme.brand.reservationCard.badge.fontWeight);
      root.style.setProperty('--reservation-badge-letter-spacing', theme.brand.reservationCard.badge.letterSpacing);
      root.style.setProperty('--reservation-badge-padding', theme.brand.reservationCard.badge.padding);
      root.style.setProperty('--reservation-confirmation-font', theme.brand.reservationCard.confirmation.font);
      root.style.setProperty('--reservation-confirmation-font-size', theme.brand.reservationCard.confirmation.fontSize);
      root.style.setProperty('--reservation-confirmation-color', theme.brand.reservationCard.confirmation.color);
      root.style.setProperty('--reservation-daterange-font', theme.brand.reservationCard.daterange.font);
      root.style.setProperty('--reservation-daterange-font-size', theme.brand.reservationCard.daterange.fontSize);
      root.style.setProperty('--reservation-daterange-color', theme.brand.reservationCard.daterange.color);
    }

    // Table
    if (theme.brand.table) {
      root.style.setProperty('--table-header-font', theme.brand.table.header.font);
      root.style.setProperty('--table-header-font-size', theme.brand.table.header.fontSize);
      root.style.setProperty('--table-header-font-weight', theme.brand.table.header.fontWeight);
      root.style.setProperty('--table-header-color', theme.brand.table.header.color);
      root.style.setProperty('--table-header-text-transform', theme.brand.table.header.textTransform);
      root.style.setProperty('--table-header-letter-spacing', theme.brand.table.header.letterSpacing);
      root.style.setProperty('--table-header-border-bottom', theme.brand.table.header.borderBottom);
      root.style.setProperty('--table-header-padding', theme.brand.table.header.padding);
      root.style.setProperty('--table-cell-font', theme.brand.table.cell.font);
      root.style.setProperty('--table-cell-font-size', theme.brand.table.cell.fontSize);
      root.style.setProperty('--table-cell-color', theme.brand.table.cell.color);
      root.style.setProperty('--table-cell-padding', theme.brand.table.cell.padding);
      root.style.setProperty('--table-cell-border-bottom', theme.brand.table.cell.borderBottom);
      root.style.setProperty('--table-row-hover', theme.brand.table.rowHover);
      root.style.setProperty('--table-stripe', theme.brand.table.stripe);
    }

    // Progress Stepper
    if (theme.brand.progressStepper) {
      root.style.setProperty('--stepper-active-text', theme.brand.progressStepper.active.text);
      root.style.setProperty('--stepper-active-bg', theme.brand.progressStepper.active.background);
      root.style.setProperty('--stepper-active-border', theme.brand.progressStepper.active.border);
      root.style.setProperty('--stepper-completed-text', theme.brand.progressStepper.completed.text);
      root.style.setProperty('--stepper-completed-bg', theme.brand.progressStepper.completed.background);
      root.style.setProperty('--stepper-completed-border', theme.brand.progressStepper.completed.border);
      root.style.setProperty('--stepper-completed-check', theme.brand.progressStepper.completed.check);
      root.style.setProperty('--stepper-pending-text', theme.brand.progressStepper.pending.text);
      root.style.setProperty('--stepper-pending-bg', theme.brand.progressStepper.pending.background);
      root.style.setProperty('--stepper-pending-border', theme.brand.progressStepper.pending.border);
      root.style.setProperty('--stepper-connector-color', theme.brand.progressStepper.connector.color);
      root.style.setProperty('--stepper-connector-completed', theme.brand.progressStepper.connector.completed);
      root.style.setProperty('--stepper-connector-width', theme.brand.progressStepper.connector.width);
      root.style.setProperty('--stepper-label-font', theme.brand.progressStepper.label.font);
      root.style.setProperty('--stepper-label-font-size', theme.brand.progressStepper.label.fontSize);
      root.style.setProperty('--stepper-label-font-weight', theme.brand.progressStepper.label.fontWeight);
      root.style.setProperty('--stepper-label-color', theme.brand.progressStepper.label.color);
      root.style.setProperty('--stepper-label-active-color', theme.brand.progressStepper.label.activeColor);
    }

    // Secondary font
    if (theme.brand.fonts.secondary) {
      root.style.setProperty('--font-secondary', theme.brand.fonts.secondary);
    }

    // ===== THEME V2.0 ADDITIONS =====

    const themeV2 = theme as BrandThemeV2;

    // CTA Text Color (new in v2.0)
    if (themeV2.brand.colors.ctaText) {
      root.style.setProperty('--brand-cta-text', themeV2.brand.colors.ctaText);
    }

    // TransactionalUI Section
    if (themeV2.transactionalUI) {
      const txUI = themeV2.transactionalUI;

      // Card
      if (txUI.card) {
        root.style.setProperty('--card-bg', txUI.card.background);
        root.style.setProperty('--card-border', txUI.card.border);
        root.style.setProperty('--card-border-radius', txUI.card.borderRadius);
        root.style.setProperty('--card-shadow', txUI.card.shadow);
        root.style.setProperty('--card-padding', txUI.card.padding);
        if (txUI.card.cornerAccent) {
          console.log('🎨 [ThemeContext] Setting corner accent (v2 legacy):', {
            color: txUI.card.cornerAccent.color,
            enabled: txUI.card.cornerAccent.enabled,
            display: txUI.card.cornerAccent.enabled ? 'block' : 'none'
          });
          root.style.setProperty('--card-corner-accent', txUI.card.cornerAccent.color);
          // Set display based on enabled flag: 'block' shows the accent, 'none' hides it
          root.style.setProperty('--card-corner-accent-display', txUI.card.cornerAccent.enabled ? 'block' : 'none');
          // Legacy variables (kept for backward compatibility)
          root.style.setProperty('--card-corner-accent-color', txUI.card.cornerAccent.color);
          root.style.setProperty('--card-corner-accent-size', txUI.card.cornerAccent.size);
          root.style.setProperty('--card-corner-accent-offset', txUI.card.cornerAccent.offset);
          root.style.setProperty('--card-corner-accent-border-width', txUI.card.cornerAccent.borderWidth);

          // Verify the value was set correctly
          const verifyColor = getComputedStyle(document.documentElement).getPropertyValue('--card-corner-accent');
          console.log('✅ [ThemeContext] Verified CSS variable (v2):', {
            '--card-corner-accent': verifyColor.trim() || '(empty)',
            expected: txUI.card.cornerAccent.color
          });
        }
      }

      // Section Heading
      if (txUI.sectionHeading) {
        root.style.setProperty('--section-heading-font', txUI.sectionHeading.fontFamily);
        root.style.setProperty('--section-heading-font-size', txUI.sectionHeading.fontSize);
        root.style.setProperty('--section-heading-font-weight', txUI.sectionHeading.fontWeight);
        root.style.setProperty('--section-heading-color', txUI.sectionHeading.color);
        root.style.setProperty('--section-heading-text-transform', txUI.sectionHeading.textTransform);
        root.style.setProperty('--section-heading-letter-spacing', txUI.sectionHeading.letterSpacing);
        if (txUI.sectionHeading.decorativeRule) {
          const rule = txUI.sectionHeading.decorativeRule;
          console.log('🎨 [ThemeContext] Setting decorative rule (v2 legacy):', {
            enabled: rule.enabled,
            lineColor: rule.lineColor,
            diamondColor: rule.diamondColor
          });
          root.style.setProperty('--decorative-rule-display', rule.enabled ? 'flex' : 'none');
          root.style.setProperty('--decorative-rule-line-width', rule.lineWidth);
          root.style.setProperty('--decorative-rule-line-color', rule.lineColor);
          root.style.setProperty('--decorative-rule-diamond-size', rule.diamondSize);
          root.style.setProperty('--decorative-rule-diamond-color', rule.diamondColor);
          // Legacy variables (kept for backward compatibility)
          root.style.setProperty('--section-heading-rule-enabled', rule.enabled.toString());
          root.style.setProperty('--section-heading-rule-width', rule.lineWidth);
          root.style.setProperty('--section-heading-rule-color', rule.lineColor);
          root.style.setProperty('--section-heading-rule-diamond-size', rule.diamondSize);
          root.style.setProperty('--section-heading-rule-diamond-color', rule.diamondColor);
        }
      }

      // Data Label
      if (txUI.dataLabel) {
        root.style.setProperty('--data-label-font', txUI.dataLabel.fontFamily);
        root.style.setProperty('--data-label-font-size', txUI.dataLabel.fontSize);
        root.style.setProperty('--data-label-font-weight', txUI.dataLabel.fontWeight);
        root.style.setProperty('--data-label-color', txUI.dataLabel.color);
        root.style.setProperty('--data-label-text-transform', txUI.dataLabel.textTransform);
        root.style.setProperty('--data-label-letter-spacing', txUI.dataLabel.letterSpacing);
      }

      // Data Value
      if (txUI.dataValue) {
        root.style.setProperty('--data-value-font', txUI.dataValue.fontFamily);
        root.style.setProperty('--data-value-font-size', txUI.dataValue.fontSize);
        root.style.setProperty('--data-value-font-weight', txUI.dataValue.fontWeight);
        root.style.setProperty('--data-value-color', txUI.dataValue.color);
        if (txUI.dataValue.variants) {
          root.style.setProperty('--data-value-highlighted-color', txUI.dataValue.variants.highlighted.color);
          root.style.setProperty('--data-value-highlighted-font-size', txUI.dataValue.variants.highlighted.fontSize);
          root.style.setProperty('--data-value-highlighted-font-weight', txUI.dataValue.variants.highlighted.fontWeight);
          root.style.setProperty('--data-value-emphasis-color', txUI.dataValue.variants.emphasis.color);
          root.style.setProperty('--data-value-emphasis-font-weight', txUI.dataValue.variants.emphasis.fontWeight);
        }
      }

      // Divider
      if (txUI.divider) {
        root.style.setProperty('--divider-vertical-width', txUI.divider.vertical.width);
        root.style.setProperty('--divider-vertical-color', txUI.divider.vertical.color);
        root.style.setProperty('--divider-horizontal-width', txUI.divider.horizontal.width);
        root.style.setProperty('--divider-horizontal-color', txUI.divider.horizontal.color);
        root.style.setProperty('--divider-accent-width', txUI.divider.accent.width);
        root.style.setProperty('--divider-accent-height', txUI.divider.accent.height);
        root.style.setProperty('--divider-accent-color', txUI.divider.accent.color);
      }

      // Info Banner
      if (txUI.infoBanner) {
        root.style.setProperty('--info-banner-bg', txUI.infoBanner.background);
        root.style.setProperty('--info-banner-border', txUI.infoBanner.border);
        root.style.setProperty('--info-banner-border-radius', txUI.infoBanner.borderRadius);
        root.style.setProperty('--info-banner-heading-color', txUI.infoBanner.headingColor);
        root.style.setProperty('--info-banner-body-color', txUI.infoBanner.bodyColor);
        root.style.setProperty('--info-banner-icon-color', txUI.infoBanner.iconColor);
        root.style.setProperty('--info-banner-icon-type', txUI.infoBanner.iconType);
      }

      // Progress Stepper (v2.0 location - overrides brand.progressStepper if present)
      if (txUI.progressStepper) {
        root.style.setProperty('--stepper-active-text', txUI.progressStepper.activeStep.textColor);
        root.style.setProperty('--stepper-active-bg', txUI.progressStepper.activeStep.background);
        root.style.setProperty('--stepper-active-border', txUI.progressStepper.activeStep.borderColor);
        root.style.setProperty('--stepper-completed-text', txUI.progressStepper.completedStep.textColor);
        root.style.setProperty('--stepper-completed-bg', txUI.progressStepper.completedStep.background);
        root.style.setProperty('--stepper-completed-border', txUI.progressStepper.completedStep.borderColor);
        root.style.setProperty('--stepper-completed-check', txUI.progressStepper.completedStep.checkColor);
        root.style.setProperty('--stepper-pending-text', txUI.progressStepper.pendingStep.textColor);
        root.style.setProperty('--stepper-pending-bg', txUI.progressStepper.pendingStep.background);
        root.style.setProperty('--stepper-pending-border', txUI.progressStepper.pendingStep.borderColor);
        root.style.setProperty('--stepper-connector-color', txUI.progressStepper.connector.color);
        root.style.setProperty('--stepper-connector-completed', txUI.progressStepper.connector.completedColor);
        root.style.setProperty('--stepper-connector-width', txUI.progressStepper.connector.width);
        root.style.setProperty('--stepper-label-font', txUI.progressStepper.label.fontFamily);
        root.style.setProperty('--stepper-label-font-size', txUI.progressStepper.label.fontSize);
        root.style.setProperty('--stepper-label-font-weight', txUI.progressStepper.label.fontWeight);
        root.style.setProperty('--stepper-label-color', txUI.progressStepper.label.color);
        root.style.setProperty('--stepper-label-active-color', txUI.progressStepper.label.activeColor);
      }

      // Button (v2.0 location)
      if (txUI.button) {
        root.style.setProperty('--button-primary-text', txUI.button.primary.text);
        root.style.setProperty('--button-primary-bg', txUI.button.primary.background);
        root.style.setProperty('--button-primary-border', txUI.button.primary.border);
        root.style.setProperty('--button-primary-hover-bg', txUI.button.primary.hoverBackground);
        root.style.setProperty('--button-primary-font-size', txUI.button.primary.fontSize);
        root.style.setProperty('--button-primary-font-weight', txUI.button.primary.fontWeight);
        root.style.setProperty('--button-primary-padding', txUI.button.primary.padding);
        root.style.setProperty('--button-primary-border-radius', txUI.button.primary.borderRadius);
        root.style.setProperty('--button-primary-letter-spacing', txUI.button.primary.letterSpacing);

        root.style.setProperty('--button-secondary-text', txUI.button.secondary.text);
        root.style.setProperty('--button-secondary-bg', txUI.button.secondary.background);
        root.style.setProperty('--button-secondary-border', txUI.button.secondary.border);
        root.style.setProperty('--button-secondary-hover-bg', txUI.button.secondary.hoverBackground);
        root.style.setProperty('--button-secondary-font-size', txUI.button.secondary.fontSize);
        root.style.setProperty('--button-secondary-font-weight', txUI.button.secondary.fontWeight);
        root.style.setProperty('--button-secondary-padding', txUI.button.secondary.padding);
        root.style.setProperty('--button-secondary-border-radius', txUI.button.secondary.borderRadius);
        root.style.setProperty('--button-secondary-letter-spacing', txUI.button.secondary.letterSpacing);

        root.style.setProperty('--button-cta-text', txUI.button.cta.text);
        root.style.setProperty('--button-cta-bg', txUI.button.cta.background);
        root.style.setProperty('--button-cta-border', txUI.button.cta.border);
        root.style.setProperty('--button-cta-hover-bg', txUI.button.cta.hoverBackground);
        root.style.setProperty('--button-cta-font-size', txUI.button.cta.fontSize);
        root.style.setProperty('--button-cta-font-weight', txUI.button.cta.fontWeight);
        root.style.setProperty('--button-cta-padding', txUI.button.cta.padding);
        root.style.setProperty('--button-cta-border-radius', txUI.button.cta.borderRadius);
        if (txUI.button.cta.minFontSize) {
          root.style.setProperty('--button-cta-min-font-size', txUI.button.cta.minFontSize);
        }

        // CTA Dark Text (new variant)
        if (txUI.button.ctaDarkText) {
          root.style.setProperty('--button-cta-dark-text', txUI.button.ctaDarkText.text);
          root.style.setProperty('--button-cta-dark-bg', txUI.button.ctaDarkText.background);
          root.style.setProperty('--button-cta-dark-border', txUI.button.ctaDarkText.border);
          root.style.setProperty('--button-cta-dark-hover-bg', txUI.button.ctaDarkText.hoverBackground);
          root.style.setProperty('--button-cta-dark-font-size', txUI.button.ctaDarkText.fontSize);
          root.style.setProperty('--button-cta-dark-font-weight', txUI.button.ctaDarkText.fontWeight);
        }
      }

      // Member Badge
      if (txUI.memberBadge) {
        root.style.setProperty('--member-badge-text', txUI.memberBadge.text);
        root.style.setProperty('--member-badge-bg', txUI.memberBadge.background);
        root.style.setProperty('--member-badge-border', txUI.memberBadge.border);
        root.style.setProperty('--member-badge-font-size', txUI.memberBadge.fontSize);
        root.style.setProperty('--member-badge-font-weight', txUI.memberBadge.fontWeight);
        root.style.setProperty('--member-badge-letter-spacing', txUI.memberBadge.letterSpacing);
        root.style.setProperty('--member-badge-text-transform', txUI.memberBadge.textTransform);
        root.style.setProperty('--member-badge-padding', txUI.memberBadge.padding);
      }

      // Reservation Card (v2.0 location)
      if (txUI.reservationCard) {
        root.style.setProperty('--reservation-image-border-radius', txUI.reservationCard.image.borderRadius);
        root.style.setProperty('--reservation-image-overlay', txUI.reservationCard.image.overlay);
        root.style.setProperty('--reservation-stay-badge-text', txUI.reservationCard.stayBadge.text);
        root.style.setProperty('--reservation-stay-badge-bg', txUI.reservationCard.stayBadge.background);
        root.style.setProperty('--reservation-stay-badge-font-size', txUI.reservationCard.stayBadge.fontSize);
        root.style.setProperty('--reservation-stay-badge-font-weight', txUI.reservationCard.stayBadge.fontWeight);
        root.style.setProperty('--reservation-stay-badge-letter-spacing', txUI.reservationCard.stayBadge.letterSpacing);
        root.style.setProperty('--reservation-stay-badge-text-transform', txUI.reservationCard.stayBadge.textTransform);
        root.style.setProperty('--reservation-stay-badge-padding', txUI.reservationCard.stayBadge.padding);
        root.style.setProperty('--reservation-confirmation-font', txUI.reservationCard.confirmationNumber.fontFamily);
        root.style.setProperty('--reservation-confirmation-font-size', txUI.reservationCard.confirmationNumber.fontSize);
        root.style.setProperty('--reservation-confirmation-color', txUI.reservationCard.confirmationNumber.color);
        root.style.setProperty('--reservation-confirmation-letter-spacing', txUI.reservationCard.confirmationNumber.letterSpacing);
        root.style.setProperty('--reservation-daterange-font', txUI.reservationCard.dateRange.fontFamily);
        root.style.setProperty('--reservation-daterange-font-size', txUI.reservationCard.dateRange.fontSize);
        root.style.setProperty('--reservation-daterange-color', txUI.reservationCard.dateRange.color);
        root.style.setProperty('--reservation-daterange-font-weight', txUI.reservationCard.dateRange.fontWeight);
      }
    }

    // Calendar (v2.0 expanded structure)
    if (themeV2.calendar) {
      const cal = themeV2.calendar;

      // Container
      if (cal.container) {
        root.style.setProperty('--calendar-container-bg', cal.container.background);
        root.style.setProperty('--calendar-container-border', cal.container.border);
        root.style.setProperty('--calendar-container-border-radius', cal.container.borderRadius);
        root.style.setProperty('--calendar-container-padding', cal.container.padding);
        root.style.setProperty('--calendar-container-shadow', cal.container.shadow);
      }

      // Month Heading
      if (cal.monthHeading) {
        root.style.setProperty('--calendar-month-font', cal.monthHeading.fontFamily);
        root.style.setProperty('--calendar-month-font-size', cal.monthHeading.fontSize);
        root.style.setProperty('--calendar-month-font-weight', cal.monthHeading.fontWeight);
        root.style.setProperty('--calendar-month-color', cal.monthHeading.color);
        root.style.setProperty('--calendar-month-letter-spacing', cal.monthHeading.letterSpacing);
      }

      // Navigation
      if (cal.navigation) {
        root.style.setProperty('--calendar-nav-color', cal.navigation.color);
        root.style.setProperty('--calendar-nav-hover-color', cal.navigation.hoverColor);
        root.style.setProperty('--calendar-nav-size', cal.navigation.size);
        root.style.setProperty('--calendar-nav-style', cal.navigation.style);
      }

      // Day of Week Header
      if (cal.dayOfWeekHeader) {
        root.style.setProperty('--calendar-day-header-font', cal.dayOfWeekHeader.fontFamily);
        root.style.setProperty('--calendar-day-header-font-size', cal.dayOfWeekHeader.fontSize);
        root.style.setProperty('--calendar-day-header-font-weight', cal.dayOfWeekHeader.fontWeight);
        root.style.setProperty('--calendar-day-header-color', cal.dayOfWeekHeader.color);
        root.style.setProperty('--calendar-day-header-text-transform', cal.dayOfWeekHeader.textTransform);
        root.style.setProperty('--calendar-day-header-letter-spacing', cal.dayOfWeekHeader.letterSpacing);
      }

      // Day Cell
      if (cal.dayCell) {
        // Default
        if (cal.dayCell.default) {
          root.style.setProperty('--calendar-day-font', cal.dayCell.default.fontFamily);
          root.style.setProperty('--calendar-day-font-size', cal.dayCell.default.fontSize);
          root.style.setProperty('--calendar-day-font-weight', cal.dayCell.default.fontWeight);
          root.style.setProperty('--calendar-day-color', cal.dayCell.default.color);
          root.style.setProperty('--calendar-day-bg', cal.dayCell.default.background);
          root.style.setProperty('--calendar-day-border', cal.dayCell.default.border);
          root.style.setProperty('--calendar-day-hover-bg', cal.dayCell.default.hoverBackground);
        }

        // Disabled
        if (cal.dayCell.disabled) {
          root.style.setProperty('--calendar-day-disabled-color', cal.dayCell.disabled.color);
          root.style.setProperty('--calendar-day-disabled-bg', cal.dayCell.disabled.background);
          root.style.setProperty('--calendar-day-disabled-cursor', cal.dayCell.disabled.cursor);
        }

        // Current Checkout
        if (cal.dayCell.currentCheckout) {
          root.style.setProperty('--calendar-day-checkout-color', cal.dayCell.currentCheckout.color);
          root.style.setProperty('--calendar-day-checkout-bg', cal.dayCell.currentCheckout.background);
          root.style.setProperty('--calendar-day-checkout-font-weight', cal.dayCell.currentCheckout.fontWeight);
          root.style.setProperty('--calendar-day-checkout-hover-bg', cal.dayCell.currentCheckout.hoverBackground);
          if (cal.dayCell.currentCheckout.subLabel) {
            root.style.setProperty('--calendar-day-checkout-sublabel-color', cal.dayCell.currentCheckout.subLabel.color);
            root.style.setProperty('--calendar-day-checkout-sublabel-font-size', cal.dayCell.currentCheckout.subLabel.fontSize);
            root.style.setProperty('--calendar-day-checkout-sublabel-font-weight', cal.dayCell.currentCheckout.subLabel.fontWeight);
            root.style.setProperty('--calendar-day-checkout-sublabel-text-transform', cal.dayCell.currentCheckout.subLabel.textTransform);
            root.style.setProperty('--calendar-day-checkout-sublabel-letter-spacing', cal.dayCell.currentCheckout.subLabel.letterSpacing);
          }
        }

        // Selected
        if (cal.dayCell.selected) {
          root.style.setProperty('--calendar-day-selected-color', cal.dayCell.selected.color);
          root.style.setProperty('--calendar-day-selected-bg', cal.dayCell.selected.background);
          root.style.setProperty('--calendar-day-selected-border', cal.dayCell.selected.border);
          root.style.setProperty('--calendar-day-selected-font-weight', cal.dayCell.selected.fontWeight);
          root.style.setProperty('--calendar-day-selected-hover-bg', cal.dayCell.selected.hoverBackground);
        }

        // Today
        if (cal.dayCell.today && cal.dayCell.today.indicator) {
          root.style.setProperty('--calendar-day-today-indicator-type', cal.dayCell.today.indicator.type);
          root.style.setProperty('--calendar-day-today-indicator-size', cal.dayCell.today.indicator.size);
          root.style.setProperty('--calendar-day-today-indicator-color', cal.dayCell.today.indicator.color);
        }

        // Range
        if (cal.dayCell.range) {
          root.style.setProperty('--calendar-day-range-bg', cal.dayCell.range.background);
          if (cal.dayCell.range.startEnd) {
            root.style.setProperty('--calendar-day-range-start-bg', cal.dayCell.range.startEnd.background);
            root.style.setProperty('--calendar-day-range-start-color', cal.dayCell.range.startEnd.color);
          }
        }
      }
    }

    // Forms (v2.0 section)
    if (themeV2.forms) {
      const forms = themeV2.forms;

      // Date Selector
      if (forms.dateSelector) {
        root.style.setProperty('--date-selector-border', forms.dateSelector.border);
        root.style.setProperty('--date-selector-bg', forms.dateSelector.background);
        root.style.setProperty('--date-selector-padding', forms.dateSelector.padding);
        root.style.setProperty('--date-selector-text-color', forms.dateSelector.textColor);
        root.style.setProperty('--date-selector-font', forms.dateSelector.fontFamily);
        root.style.setProperty('--date-selector-font-size', forms.dateSelector.fontSize);
        root.style.setProperty('--date-selector-font-weight', forms.dateSelector.fontWeight);
        if (forms.dateSelector.hover) {
          root.style.setProperty('--date-selector-hover-bg', forms.dateSelector.hover.background);
          root.style.setProperty('--date-selector-hover-border', forms.dateSelector.hover.borderColor);
        }
        if (forms.dateSelector.chevron) {
          root.style.setProperty('--date-selector-chevron-color', forms.dateSelector.chevron.color);
          root.style.setProperty('--date-selector-chevron-position', forms.dateSelector.chevron.position);
        }
      }

      // Text Input
      if (forms.textInput) {
        root.style.setProperty('--text-input-border', forms.textInput.border);
        root.style.setProperty('--text-input-bg', forms.textInput.background);
        root.style.setProperty('--text-input-focus-border', forms.textInput.focusBorder);
        root.style.setProperty('--text-input-focus-shadow', forms.textInput.focusShadow);
        root.style.setProperty('--text-input-text-color', forms.textInput.textColor);
        root.style.setProperty('--text-input-placeholder-color', forms.textInput.placeholderColor);
        root.style.setProperty('--text-input-font-size', forms.textInput.fontSize);
        root.style.setProperty('--text-input-padding', forms.textInput.padding);
        root.style.setProperty('--text-input-border-radius', forms.textInput.borderRadius);
      }

      // Select
      if (forms.select) {
        root.style.setProperty('--select-border', forms.select.border);
        root.style.setProperty('--select-bg', forms.select.background);
        root.style.setProperty('--select-text-color', forms.select.textColor);
        root.style.setProperty('--select-chevron-color', forms.select.chevronColor);
        root.style.setProperty('--select-focus-border', forms.select.focusBorder);
        root.style.setProperty('--select-option-hover', forms.select.optionHover);
      }

      // Checkbox
      if (forms.checkbox) {
        root.style.setProperty('--checkbox-border', forms.checkbox.border);
        root.style.setProperty('--checkbox-checked-bg', forms.checkbox.checkedBackground);
        root.style.setProperty('--checkbox-checked-border', forms.checkbox.checkedBorder);
        root.style.setProperty('--checkbox-check-color', forms.checkbox.checkColor);
        root.style.setProperty('--checkbox-size', forms.checkbox.size);
      }
    }

    // Table (v2.0 location)
    if (themeV2.table) {
      root.style.setProperty('--table-header-font', themeV2.table.header.fontFamily);
      root.style.setProperty('--table-header-font-size', themeV2.table.header.fontSize);
      root.style.setProperty('--table-header-font-weight', themeV2.table.header.fontWeight);
      root.style.setProperty('--table-header-color', themeV2.table.header.color);
      root.style.setProperty('--table-header-text-transform', themeV2.table.header.textTransform);
      root.style.setProperty('--table-header-letter-spacing', themeV2.table.header.letterSpacing);
      root.style.setProperty('--table-header-border-bottom', themeV2.table.header.borderBottom);
      root.style.setProperty('--table-header-padding', themeV2.table.header.padding);
      root.style.setProperty('--table-cell-font', themeV2.table.cell.fontFamily);
      root.style.setProperty('--table-cell-font-size', themeV2.table.cell.fontSize);
      root.style.setProperty('--table-cell-color', themeV2.table.cell.color);
      root.style.setProperty('--table-cell-padding', themeV2.table.cell.padding);
      root.style.setProperty('--table-cell-border-bottom', themeV2.table.cell.borderBottom);
      root.style.setProperty('--table-row-hover', themeV2.table.rowHover);
      root.style.setProperty('--table-stripe', themeV2.table.stripe);
    }

    // Pricing (v2.0 location with perUnit addition)
    if (themeV2.pricing) {
      root.style.setProperty('--pricing-amount-font', themeV2.pricing.amount.fontFamily);
      root.style.setProperty('--pricing-amount-font-size', themeV2.pricing.amount.fontSize);
      root.style.setProperty('--pricing-amount-font-weight', themeV2.pricing.amount.fontWeight);
      root.style.setProperty('--pricing-amount-color', themeV2.pricing.amount.color);
      root.style.setProperty('--pricing-currency-font-size', themeV2.pricing.currency.fontSize);
      root.style.setProperty('--pricing-currency-font-weight', themeV2.pricing.currency.fontWeight);
      root.style.setProperty('--pricing-currency-color', themeV2.pricing.currency.color);
      if (themeV2.pricing.perUnit) {
        root.style.setProperty('--pricing-per-unit-font-size', themeV2.pricing.perUnit.fontSize);
        root.style.setProperty('--pricing-per-unit-color', themeV2.pricing.perUnit.color);
      }
      root.style.setProperty('--pricing-total-font-size', themeV2.pricing.total.fontSize);
      root.style.setProperty('--pricing-total-font-weight', themeV2.pricing.total.fontWeight);
      root.style.setProperty('--pricing-total-color', themeV2.pricing.total.color);
      root.style.setProperty('--pricing-strikethrough-color', themeV2.pricing.strikethrough.color);
      if (themeV2.pricing.strikethrough.textDecoration) {
        root.style.setProperty('--pricing-strikethrough-decoration', themeV2.pricing.strikethrough.textDecoration);
      }
      root.style.setProperty('--pricing-savings-color', themeV2.pricing.savings.color);
      root.style.setProperty('--pricing-savings-bg', themeV2.pricing.savings.background);
      root.style.setProperty('--pricing-savings-font-size', themeV2.pricing.savings.fontSize);
      root.style.setProperty('--pricing-savings-font-weight', themeV2.pricing.savings.fontWeight);
    }

    // AI Assistant (v2.0 with logoUrl)
    if (themeV2.aiAssistant.logoUrl) {
      root.style.setProperty('--ai-assistant-logo-url', themeV2.aiAssistant.logoUrl);
    }

    // ========================================
    // NEW IN v3.0: Additional theme sections
    // ========================================
    const themeV3 = theme as any; // Type assertion for v3.0 properties

    // Status colors (v3.0)
    if (themeV3.status) {
      root.style.setProperty('--status-error', themeV3.status.error);
      root.style.setProperty('--status-error-light', themeV3.status.errorLight);
      root.style.setProperty('--status-error-bg', themeV3.status.errorBg);
      root.style.setProperty('--status-warning', themeV3.status.warning);
      root.style.setProperty('--status-warning-light', themeV3.status.warningLight);
      root.style.setProperty('--status-warning-bg', themeV3.status.warningBg);
      root.style.setProperty('--status-success', themeV3.status.success);
      root.style.setProperty('--status-success-light', themeV3.status.successLight);
      root.style.setProperty('--status-success-bg', themeV3.status.successBg);
      root.style.setProperty('--status-info', themeV3.status.info);
      root.style.setProperty('--status-info-light', themeV3.status.infoLight);
      root.style.setProperty('--status-info-bg', themeV3.status.infoBg);
    }

    // Modal (v3.0)
    if (themeV3.modal) {
      root.style.setProperty('--modal-backdrop-bg', themeV3.modal.backdrop.background);
      root.style.setProperty('--modal-container-bg', themeV3.modal.container.background);
      root.style.setProperty('--modal-container-border', themeV3.modal.container.border);
      root.style.setProperty('--modal-container-shadow', themeV3.modal.container.shadow);
      root.style.setProperty('--modal-container-border-radius', themeV3.modal.container.borderRadius);
      root.style.setProperty('--modal-container-padding', themeV3.modal.container.padding);
      root.style.setProperty('--modal-container-max-width', themeV3.modal.container.maxWidth);
      root.style.setProperty('--modal-header-border-bottom', themeV3.modal.header.borderBottom);
      root.style.setProperty('--modal-header-title-color', themeV3.modal.header.titleColor);
      root.style.setProperty('--modal-header-title-font', themeV3.modal.header.titleFontFamily);
      root.style.setProperty('--modal-header-title-font-size', themeV3.modal.header.titleFontSize);
      root.style.setProperty('--modal-header-title-font-weight', themeV3.modal.header.titleFontWeight);
      root.style.setProperty('--modal-header-close-color', themeV3.modal.header.closeButtonColor);
      root.style.setProperty('--modal-header-close-hover-color', themeV3.modal.header.closeButtonHoverColor);
      root.style.setProperty('--modal-body-text-color', themeV3.modal.body.textColor);
      root.style.setProperty('--modal-body-font-size', themeV3.modal.body.fontSize);
      root.style.setProperty('--modal-body-line-height', themeV3.modal.body.lineHeight);
      root.style.setProperty('--modal-footer-border-top', themeV3.modal.footer.borderTop);
      root.style.setProperty('--modal-footer-padding', themeV3.modal.footer.padding);
    }

    // Dropdown (v3.0)
    if (themeV3.dropdown) {
      root.style.setProperty('--dropdown-container-bg', themeV3.dropdown.container.background);
      root.style.setProperty('--dropdown-container-border', themeV3.dropdown.container.border);
      root.style.setProperty('--dropdown-container-shadow', themeV3.dropdown.container.shadow);
      root.style.setProperty('--dropdown-container-border-radius', themeV3.dropdown.container.borderRadius);
      root.style.setProperty('--dropdown-container-max-height', themeV3.dropdown.container.maxHeight);
      root.style.setProperty('--dropdown-option-text', themeV3.dropdown.option.text);
      root.style.setProperty('--dropdown-option-hover-bg', themeV3.dropdown.option.hoverBackground);
      root.style.setProperty('--dropdown-option-selected-bg', themeV3.dropdown.option.selectedBackground);
      root.style.setProperty('--dropdown-option-selected-text', themeV3.dropdown.option.selectedText);
      root.style.setProperty('--dropdown-option-padding', themeV3.dropdown.option.padding);
      root.style.setProperty('--dropdown-option-font-size', themeV3.dropdown.option.fontSize);
      root.style.setProperty('--dropdown-divider', themeV3.dropdown.divider);
      root.style.setProperty('--dropdown-arrow-color', themeV3.dropdown.arrow.color);
      root.style.setProperty('--dropdown-arrow-size', themeV3.dropdown.arrow.size);
    }

    // Link (v3.0)
    if (themeV3.link) {
      root.style.setProperty('--link-color', themeV3.link.color);
      root.style.setProperty('--link-hover-color', themeV3.link.hoverColor);
      root.style.setProperty('--link-visited-color', themeV3.link.visitedColor);
      root.style.setProperty('--link-underline', themeV3.link.underline);
      root.style.setProperty('--link-hover-underline', themeV3.link.hoverUnderline);
      root.style.setProperty('--link-font-size', themeV3.link.fontSize);
      root.style.setProperty('--link-font-weight', themeV3.link.fontWeight);
    }

    // Loading (v3.0)
    if (themeV3.loading) {
      root.style.setProperty('--loading-skeleton-bg', themeV3.loading.skeleton.background);
      root.style.setProperty('--loading-skeleton-highlight', themeV3.loading.skeleton.highlight);
      root.style.setProperty('--loading-skeleton-border-radius', themeV3.loading.skeleton.borderRadius);
      root.style.setProperty('--loading-skeleton-animation', themeV3.loading.skeleton.animation);
      root.style.setProperty('--loading-spinner-primary', themeV3.loading.spinner.primary);
      root.style.setProperty('--loading-spinner-secondary', themeV3.loading.spinner.secondary);
      root.style.setProperty('--loading-spinner-size', themeV3.loading.spinner.size);
      root.style.setProperty('--loading-spinner-stroke-width', themeV3.loading.spinner.strokeWidth);
      root.style.setProperty('--loading-overlay-bg', themeV3.loading.overlay.background);
    }

    // Tooltip (v3.0)
    if (themeV3.tooltip) {
      root.style.setProperty('--tooltip-bg', themeV3.tooltip.background);
      root.style.setProperty('--tooltip-text', themeV3.tooltip.text);
      root.style.setProperty('--tooltip-border', themeV3.tooltip.border);
      root.style.setProperty('--tooltip-shadow', themeV3.tooltip.shadow);
      root.style.setProperty('--tooltip-font-size', themeV3.tooltip.fontSize);
      root.style.setProperty('--tooltip-padding', themeV3.tooltip.padding);
      root.style.setProperty('--tooltip-border-radius', themeV3.tooltip.borderRadius);
      root.style.setProperty('--tooltip-max-width', themeV3.tooltip.maxWidth);
    }

    // Toggle (v3.0)
    if (themeV3.toggle) {
      root.style.setProperty('--toggle-bg-off', themeV3.toggle.backgroundOff);
      root.style.setProperty('--toggle-bg-on', themeV3.toggle.backgroundOn);
      root.style.setProperty('--toggle-handle-bg', themeV3.toggle.handle.background);
      root.style.setProperty('--toggle-handle-size', themeV3.toggle.handle.size);
      root.style.setProperty('--toggle-border', themeV3.toggle.border);
      root.style.setProperty('--toggle-width', themeV3.toggle.width);
      root.style.setProperty('--toggle-height', themeV3.toggle.height);
    }

    // Forms - Additional v3.0 properties
    if (themeV3.forms) {
      // Radio buttons (v3.0)
      if (themeV3.forms.radio) {
        root.style.setProperty('--radio-border', themeV3.forms.radio.border);
        root.style.setProperty('--radio-checked-bg', themeV3.forms.radio.checkedBackground);
        root.style.setProperty('--radio-checked-border', themeV3.forms.radio.checkedBorder);
        root.style.setProperty('--radio-dot-color', themeV3.forms.radio.dotColor);
        root.style.setProperty('--radio-size', themeV3.forms.radio.size);
        root.style.setProperty('--radio-dot-size', themeV3.forms.radio.dotSize);
      }

      // Error states (v3.0)
      if (themeV3.forms.errorStates) {
        root.style.setProperty('--form-error-border', themeV3.forms.errorStates.border);
        root.style.setProperty('--form-error-bg', themeV3.forms.errorStates.background);
        root.style.setProperty('--form-error-text', themeV3.forms.errorStates.text);
        root.style.setProperty('--form-error-focus-shadow', themeV3.forms.errorStates.focusShadow);
      }

      // Success states (v3.0)
      if (themeV3.forms.successStates) {
        root.style.setProperty('--form-success-border', themeV3.forms.successStates.border);
        root.style.setProperty('--form-success-bg', themeV3.forms.successStates.background);
        root.style.setProperty('--form-success-focus-shadow', themeV3.forms.successStates.focusShadow);
      }

      // Disabled states (v3.0)
      if (themeV3.forms.disabledStates) {
        root.style.setProperty('--form-disabled-bg', themeV3.forms.disabledStates.background);
        root.style.setProperty('--form-disabled-border', themeV3.forms.disabledStates.border);
        root.style.setProperty('--form-disabled-text', themeV3.forms.disabledStates.text);
      }

      // Checkbox border radius (v3.0)
      if (themeV3.forms.checkbox?.borderRadius) {
        root.style.setProperty('--checkbox-border-radius', themeV3.forms.checkbox.borderRadius);
      }
    }

    // ========================================
    // NEW IN v3.1: Additional theme sections
    // ========================================

    // Neutrals (v3.1)
    if (themeV3.neutrals) {
      root.style.setProperty('--neutral-gray-50', themeV3.neutrals.gray50);
      root.style.setProperty('--neutral-gray-100', themeV3.neutrals.gray100);
      root.style.setProperty('--neutral-gray-200', themeV3.neutrals.gray200);
      root.style.setProperty('--neutral-gray-300', themeV3.neutrals.gray300);
      root.style.setProperty('--neutral-gray-400', themeV3.neutrals.gray400);
      root.style.setProperty('--neutral-gray-500', themeV3.neutrals.gray500);
      root.style.setProperty('--neutral-gray-600', themeV3.neutrals.gray600);
      root.style.setProperty('--neutral-gray-700', themeV3.neutrals.gray700);
      root.style.setProperty('--neutral-gray-800', themeV3.neutrals.gray800);
      root.style.setProperty('--neutral-gray-900', themeV3.neutrals.gray900);
      root.style.setProperty('--neutral-white', themeV3.neutrals.white);
      root.style.setProperty('--neutral-black', themeV3.neutrals.black);

      // New in updated v3.1
      root.style.setProperty('--neutral-warm-white', themeV3.neutrals.warmWhite);
      root.style.setProperty('--neutral-off-white', themeV3.neutrals.offWhite);
      root.style.setProperty('--neutral-cream', themeV3.neutrals.cream);
      root.style.setProperty('--neutral-charcoal', themeV3.neutrals.charcoal);
      root.style.setProperty('--neutral-slate', themeV3.neutrals.slate);
      root.style.setProperty('--neutral-steel', themeV3.neutrals.steel);
      root.style.setProperty('--neutral-graphite', themeV3.neutrals.graphite);
      root.style.setProperty('--neutral-border-light', themeV3.neutrals.borderLight);
      root.style.setProperty('--neutral-border-medium', themeV3.neutrals.borderMedium);
      root.style.setProperty('--neutral-border-dark', themeV3.neutrals.borderDark);

      if (themeV3.neutrals.overlay) {
        root.style.setProperty('--neutral-overlay-light', themeV3.neutrals.overlay.light);
        root.style.setProperty('--neutral-overlay-medium', themeV3.neutrals.overlay.medium);
        root.style.setProperty('--neutral-overlay-heavy', themeV3.neutrals.overlay.heavy);
      }

      if (themeV3.neutrals.divider) {
        root.style.setProperty('--neutral-divider-subtle', themeV3.neutrals.divider.subtle);
        root.style.setProperty('--neutral-divider-default', themeV3.neutrals.divider.default);
        root.style.setProperty('--neutral-divider-strong', themeV3.neutrals.divider.strong);
      }
    }

    // Celebrations (v3.1)
    if (themeV3.celebrations) {
      if (themeV3.celebrations.birthday) {
        root.style.setProperty('--celebration-birthday-primary', themeV3.celebrations.birthday.primary);
        root.style.setProperty('--celebration-birthday-secondary', themeV3.celebrations.birthday.secondary);
        root.style.setProperty('--celebration-birthday-tertiary', themeV3.celebrations.birthday.tertiary);
        root.style.setProperty('--celebration-birthday-light', themeV3.celebrations.birthday.light);
        root.style.setProperty('--celebration-birthday-lighter', themeV3.celebrations.birthday.lighter);
        root.style.setProperty('--celebration-birthday-bg', themeV3.celebrations.birthday.background);
        root.style.setProperty('--celebration-birthday-border', themeV3.celebrations.birthday.border);
      }

      if (themeV3.celebrations.anniversary) {
        root.style.setProperty('--celebration-anniversary-primary', themeV3.celebrations.anniversary.primary);
        root.style.setProperty('--celebration-anniversary-secondary', themeV3.celebrations.anniversary.secondary);
        root.style.setProperty('--celebration-anniversary-tertiary', themeV3.celebrations.anniversary.tertiary);
        root.style.setProperty('--celebration-anniversary-light', themeV3.celebrations.anniversary.light);
        root.style.setProperty('--celebration-anniversary-accent', themeV3.celebrations.anniversary.accent);
        root.style.setProperty('--celebration-anniversary-bg', themeV3.celebrations.anniversary.background);
        root.style.setProperty('--celebration-anniversary-border', themeV3.celebrations.anniversary.border);
      }

      if (themeV3.celebrations.wedding) {
        root.style.setProperty('--celebration-wedding-primary', themeV3.celebrations.wedding.primary);
        root.style.setProperty('--celebration-wedding-secondary', themeV3.celebrations.wedding.secondary);
        root.style.setProperty('--celebration-wedding-light', themeV3.celebrations.wedding.light);
        root.style.setProperty('--celebration-wedding-accent', themeV3.celebrations.wedding.accent);
        root.style.setProperty('--celebration-wedding-bg', themeV3.celebrations.wedding.background);
        root.style.setProperty('--celebration-wedding-border', themeV3.celebrations.wedding.border);
      }
    }

    // Services (v3.1)
    if (themeV3.services) {
      if (themeV3.services.lighting) {
        root.style.setProperty('--service-lighting-primary', themeV3.services.lighting.primary);
        root.style.setProperty('--service-lighting-secondary', themeV3.services.lighting.secondary);
        root.style.setProperty('--service-lighting-dark', themeV3.services.lighting.dark);
        root.style.setProperty('--service-lighting-darker', themeV3.services.lighting.darker);
        root.style.setProperty('--service-lighting-light', themeV3.services.lighting.light);
        root.style.setProperty('--service-lighting-bg', themeV3.services.lighting.background);
        root.style.setProperty('--service-lighting-border', themeV3.services.lighting.border);
        root.style.setProperty('--service-lighting-icon', themeV3.services.lighting.icon);
        root.style.setProperty('--service-lighting-text', themeV3.services.lighting.text);
      }

      if (themeV3.services.ac) {
        root.style.setProperty('--service-ac-primary', themeV3.services.ac.primary);
        root.style.setProperty('--service-ac-secondary', themeV3.services.ac.secondary);
        root.style.setProperty('--service-ac-dark', themeV3.services.ac.dark);
        root.style.setProperty('--service-ac-darker', themeV3.services.ac.darker);
        root.style.setProperty('--service-ac-light', themeV3.services.ac.light);
        root.style.setProperty('--service-ac-bg', themeV3.services.ac.background);
        root.style.setProperty('--service-ac-border', themeV3.services.ac.border);
        root.style.setProperty('--service-ac-icon', themeV3.services.ac.icon);
        root.style.setProperty('--service-ac-text', themeV3.services.ac.text);
      }

      if (themeV3.services.heater) {
        root.style.setProperty('--service-heater-primary', themeV3.services.heater.primary);
        root.style.setProperty('--service-heater-secondary', themeV3.services.heater.secondary);
        root.style.setProperty('--service-heater-dark', themeV3.services.heater.dark);
        root.style.setProperty('--service-heater-darker', themeV3.services.heater.darker);
        root.style.setProperty('--service-heater-light', themeV3.services.heater.light);
        root.style.setProperty('--service-heater-bg', themeV3.services.heater.background);
        root.style.setProperty('--service-heater-border', themeV3.services.heater.border);
        root.style.setProperty('--service-heater-icon', themeV3.services.heater.icon);
        root.style.setProperty('--service-heater-text', themeV3.services.heater.text);
      }

      if (themeV3.services.tv) {
        root.style.setProperty('--service-tv-primary', themeV3.services.tv.primary);
        root.style.setProperty('--service-tv-secondary', themeV3.services.tv.secondary);
        root.style.setProperty('--service-tv-dark', themeV3.services.tv.dark);
        root.style.setProperty('--service-tv-darker', themeV3.services.tv.darker);
        root.style.setProperty('--service-tv-light', themeV3.services.tv.light);
        root.style.setProperty('--service-tv-bg', themeV3.services.tv.background);
        root.style.setProperty('--service-tv-border', themeV3.services.tv.border);
        root.style.setProperty('--service-tv-icon', themeV3.services.tv.icon);
        root.style.setProperty('--service-tv-text', themeV3.services.tv.text);
      }

      if (themeV3.services.wifi) {
        root.style.setProperty('--service-wifi-primary', themeV3.services.wifi.primary);
        root.style.setProperty('--service-wifi-secondary', themeV3.services.wifi.secondary);
        root.style.setProperty('--service-wifi-dark', themeV3.services.wifi.dark);
        root.style.setProperty('--service-wifi-darker', themeV3.services.wifi.darker);
        root.style.setProperty('--service-wifi-light', themeV3.services.wifi.light);
        root.style.setProperty('--service-wifi-bg', themeV3.services.wifi.background);
        root.style.setProperty('--service-wifi-border', themeV3.services.wifi.border);
        root.style.setProperty('--service-wifi-icon', themeV3.services.wifi.icon);
        root.style.setProperty('--service-wifi-text', themeV3.services.wifi.text);
      }
    }

    // Dining (v3.1) - Updated structure: dining.outlets.default
    if (themeV3.dining?.outlets?.default) {
      root.style.setProperty('--dining-outlet-brand', themeV3.dining.outlets.default.brand);
      root.style.setProperty('--dining-outlet-brand-light', themeV3.dining.outlets.default.brandLight);
      root.style.setProperty('--dining-outlet-accent', themeV3.dining.outlets.default.accent);
      root.style.setProperty('--dining-outlet-accent-light', themeV3.dining.outlets.default.accentLight);
      root.style.setProperty('--dining-outlet-background', themeV3.dining.outlets.default.background);
      root.style.setProperty('--dining-outlet-surface', themeV3.dining.outlets.default.surface);
      root.style.setProperty('--dining-outlet-text', themeV3.dining.outlets.default.text);
      root.style.setProperty('--dining-outlet-border', themeV3.dining.outlets.default.border);
    }

    // Auth (v3.1)
    if (themeV3.auth) {
      if (themeV3.auth.modalOverrides) {
        root.style.setProperty('--auth-modal-bg', themeV3.auth.modalOverrides.background);
        root.style.setProperty('--auth-modal-border', themeV3.auth.modalOverrides.border);
        root.style.setProperty('--auth-modal-shadow', themeV3.auth.modalOverrides.shadow);
        root.style.setProperty('--auth-modal-title-color', themeV3.auth.modalOverrides.titleColor);
        root.style.setProperty('--auth-modal-body-color', themeV3.auth.modalOverrides.bodyColor);
        root.style.setProperty('--auth-modal-divider-color', themeV3.auth.modalOverrides.dividerColor);
      }

      if (themeV3.auth.button) {
        if (themeV3.auth.button.primary) {
          root.style.setProperty('--auth-btn-primary-bg', themeV3.auth.button.primary.background);
          root.style.setProperty('--auth-btn-primary-text', themeV3.auth.button.primary.text);
          root.style.setProperty('--auth-btn-primary-border', themeV3.auth.button.primary.border);
          root.style.setProperty('--auth-btn-primary-hover-bg', themeV3.auth.button.primary.hoverBackground);
        }

        if (themeV3.auth.button.secondary) {
          root.style.setProperty('--auth-btn-secondary-bg', themeV3.auth.button.secondary.background);
          root.style.setProperty('--auth-btn-secondary-text', themeV3.auth.button.secondary.text);
          root.style.setProperty('--auth-btn-secondary-border', themeV3.auth.button.secondary.border);
          root.style.setProperty('--auth-btn-secondary-hover-bg', themeV3.auth.button.secondary.hoverBackground);
        }

        if (themeV3.auth.button.cancel) {
          root.style.setProperty('--auth-btn-cancel-bg', themeV3.auth.button.cancel.background);
          root.style.setProperty('--auth-btn-cancel-text', themeV3.auth.button.cancel.text);
          root.style.setProperty('--auth-btn-cancel-border', themeV3.auth.button.cancel.border);
          root.style.setProperty('--auth-btn-cancel-hover-bg', themeV3.auth.button.cancel.hoverBackground);
        }
      }

      if (themeV3.auth.signOut) {
        root.style.setProperty('--auth-signout-surface-primary', themeV3.auth.signOut.surfacePrimary);
        root.style.setProperty('--auth-signout-surface-secondary', themeV3.auth.signOut.surfaceSecondary);
        root.style.setProperty('--auth-signout-brand-accent', themeV3.auth.signOut.brandAccent);
        root.style.setProperty('--auth-signout-decorative-accent', themeV3.auth.signOut.decorativeAccent);
        root.style.setProperty('--auth-signout-positive-action', themeV3.auth.signOut.positiveAction);
        root.style.setProperty('--auth-signout-text-primary', themeV3.auth.signOut.textPrimary);
        root.style.setProperty('--auth-signout-text-secondary', themeV3.auth.signOut.textSecondary);
        root.style.setProperty('--auth-signout-border', themeV3.auth.signOut.border);
      }
    }

    // Profile (v3.1)
    if (themeV3.profile) {
      if (themeV3.profile.card) {
        root.style.setProperty('--profile-card-bg', themeV3.profile.card.background);
        root.style.setProperty('--profile-card-border', themeV3.profile.card.border);
        root.style.setProperty('--profile-card-shadow', themeV3.profile.card.shadow);
        root.style.setProperty('--profile-card-label-color', themeV3.profile.card.labelColor);
        root.style.setProperty('--profile-card-value-color', themeV3.profile.card.valueColor);
      }

      if (themeV3.profile.membership) {
        root.style.setProperty('--profile-membership-gold', themeV3.profile.membership.gold);
        root.style.setProperty('--profile-membership-silver', themeV3.profile.membership.silver);
        root.style.setProperty('--profile-membership-bronze', themeV3.profile.membership.bronze);
        root.style.setProperty('--profile-membership-standard', themeV3.profile.membership.standard);
      }

      if (themeV3.profile.status) {
        root.style.setProperty('--profile-status-verified', themeV3.profile.status.verified);
        root.style.setProperty('--profile-status-pending', themeV3.profile.status.pending);
        root.style.setProperty('--profile-status-incomplete', themeV3.profile.status.incomplete);
      }
    }

    // Workflows (v3.1)
    if (themeV3.workflows) {
      if (themeV3.workflows.checkin) {
        root.style.setProperty('--workflow-checkin-primary', themeV3.workflows.checkin.primary);
        root.style.setProperty('--workflow-checkin-secondary', themeV3.workflows.checkin.secondary);
        root.style.setProperty('--workflow-checkin-bg', themeV3.workflows.checkin.background);
        root.style.setProperty('--workflow-checkin-success-bg', themeV3.workflows.checkin.successBackground);
        root.style.setProperty('--workflow-checkin-icon', themeV3.workflows.checkin.icon);
        root.style.setProperty('--workflow-checkin-text', themeV3.workflows.checkin.text);

        if (themeV3.workflows.checkin.earlyCheckIn) {
          root.style.setProperty('--workflow-checkin-early-bg', themeV3.workflows.checkin.earlyCheckIn.background);
          root.style.setProperty('--workflow-checkin-early-border', themeV3.workflows.checkin.earlyCheckIn.border);
          root.style.setProperty('--workflow-checkin-early-text', themeV3.workflows.checkin.earlyCheckIn.text);
          root.style.setProperty('--workflow-checkin-early-icon', themeV3.workflows.checkin.earlyCheckIn.icon);
        }

        if (themeV3.workflows.checkin.lateCheckIn) {
          root.style.setProperty('--workflow-checkin-late-bg', themeV3.workflows.checkin.lateCheckIn.background);
          root.style.setProperty('--workflow-checkin-late-border', themeV3.workflows.checkin.lateCheckIn.border);
          root.style.setProperty('--workflow-checkin-late-text', themeV3.workflows.checkin.lateCheckIn.text);
          root.style.setProperty('--workflow-checkin-late-icon', themeV3.workflows.checkin.lateCheckIn.icon);
        }
      }

      if (themeV3.workflows.checkout) {
        root.style.setProperty('--workflow-checkout-primary', themeV3.workflows.checkout.primary);
        root.style.setProperty('--workflow-checkout-secondary', themeV3.workflows.checkout.secondary);
        root.style.setProperty('--workflow-checkout-bg', themeV3.workflows.checkout.background);
        root.style.setProperty('--workflow-checkout-success-bg', themeV3.workflows.checkout.successBackground);
        root.style.setProperty('--workflow-checkout-icon', themeV3.workflows.checkout.icon);
        root.style.setProperty('--workflow-checkout-text', themeV3.workflows.checkout.text);

        if (themeV3.workflows.checkout.lateCheckout) {
          root.style.setProperty('--workflow-checkout-late-bg', themeV3.workflows.checkout.lateCheckout.background);
          root.style.setProperty('--workflow-checkout-late-border', themeV3.workflows.checkout.lateCheckout.border);
          root.style.setProperty('--workflow-checkout-late-text', themeV3.workflows.checkout.lateCheckout.text);
          root.style.setProperty('--workflow-checkout-late-icon', themeV3.workflows.checkout.lateCheckout.icon);
        }
      }

      if (themeV3.workflows.upgrade) {
        root.style.setProperty('--workflow-upgrade-primary', themeV3.workflows.upgrade.primary);
        root.style.setProperty('--workflow-upgrade-secondary', themeV3.workflows.upgrade.secondary);
        root.style.setProperty('--workflow-upgrade-bg', themeV3.workflows.upgrade.background);

        if (themeV3.workflows.upgrade.badge) {
          root.style.setProperty('--workflow-upgrade-badge-bg', themeV3.workflows.upgrade.badge.background);
          root.style.setProperty('--workflow-upgrade-badge-text', themeV3.workflows.upgrade.badge.text);
          root.style.setProperty('--workflow-upgrade-badge-border', themeV3.workflows.upgrade.badge.border);
        }

        if (themeV3.workflows.upgrade.comparison) {
          root.style.setProperty('--workflow-upgrade-comparison-current', themeV3.workflows.upgrade.comparison.current);
          root.style.setProperty('--workflow-upgrade-comparison-upgraded', themeV3.workflows.upgrade.comparison.upgraded);
          root.style.setProperty('--workflow-upgrade-comparison-benefit', themeV3.workflows.upgrade.comparison.benefit);
        }
      }
    }

    // Events (v3.1)
    if (themeV3.events) {
      if (themeV3.events.business) {
        root.style.setProperty('--events-business-primary', themeV3.events.business.primary);
        root.style.setProperty('--events-business-secondary', themeV3.events.business.secondary);
        root.style.setProperty('--events-business-bg', themeV3.events.business.background);
        root.style.setProperty('--events-business-border', themeV3.events.business.border);
        root.style.setProperty('--events-business-icon', themeV3.events.business.icon);
        root.style.setProperty('--events-business-text', themeV3.events.business.text);
      }

      if (themeV3.events.indicators) {
        root.style.setProperty('--events-indicator-attendees', themeV3.events.indicators.attendees);
        root.style.setProperty('--events-indicator-duration', themeV3.events.indicators.duration);
        root.style.setProperty('--events-indicator-capacity', themeV3.events.indicators.capacity);
        root.style.setProperty('--events-indicator-status', themeV3.events.indicators.status);
      }
    }

    // ChangeRequests (v3.1)
    if (themeV3.changeRequests) {
      root.style.setProperty('--change-request-primary', themeV3.changeRequests.primary);
      root.style.setProperty('--change-request-secondary', themeV3.changeRequests.secondary);
      root.style.setProperty('--change-request-bg', themeV3.changeRequests.background);
      root.style.setProperty('--change-request-border', themeV3.changeRequests.border);
      root.style.setProperty('--change-request-icon', themeV3.changeRequests.icon);
      root.style.setProperty('--change-request-text', themeV3.changeRequests.text);

      if (themeV3.changeRequests.status) {
        root.style.setProperty('--change-request-status-pending', themeV3.changeRequests.status.pending);
        root.style.setProperty('--change-request-status-approved', themeV3.changeRequests.status.approved);
        root.style.setProperty('--change-request-status-rejected', themeV3.changeRequests.status.rejected);
        root.style.setProperty('--change-request-status-processing', themeV3.changeRequests.status.processing);
      }
    }
  };

  const loadTheme = async (tenantSlug: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // Check localStorage cache first (with TTL)
      const cacheKey = `theme_${tenantSlug}`;
      const cached = localStorage.getItem(cacheKey);
      const cacheExpiry = localStorage.getItem(`${cacheKey}_expiry`);
      
      if (cached && cacheExpiry && new Date().getTime() < parseInt(cacheExpiry)) {
        const cachedTheme = JSON.parse(cached);
        setTheme(cachedTheme);
        applyCSSVariables(cachedTheme);
        setIsLoading(false);
        return;
      }

      // Load brand-specific theme based on tenant slug
      console.log(`Loading theme for tenant: ${tenantSlug}`);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Get brand-specific theme
      const loadedTheme = getBrandTheme(tenantSlug);

      // Cache for 1 hour
      const expiryTime = new Date().getTime() + (60 * 60 * 1000);
      localStorage.setItem(cacheKey, JSON.stringify(loadedTheme));
      localStorage.setItem(`${cacheKey}_expiry`, expiryTime.toString());

      setTheme(loadedTheme);
      applyCSSVariables(loadedTheme);

    } catch (err) {
      console.warn('Failed to load theme, using defaults:', err);
      setError(err instanceof Error ? err.message : 'Failed to load theme');
      
      // Apply default theme on error
      setTheme(DEFAULT_THEME);
      applyCSSVariables(DEFAULT_THEME);
    } finally {
      setIsLoading(false);
    }
  };

  const setThemeFromTenantInfo = (tenantInfo: TenantInfo) => {
    try {
      const flattened = flattenLocaleMapsShallow(tenantInfo, i18n.language);
      console.log('🎨 Setting theme from tenant info:', flattened.name);

      const newTheme = createThemeFromTenantInfo(flattened);
      setTheme(newTheme);
      setTenantData(flattened);
      applyCSSVariables(newTheme);
      setError(null);

      // Cache the theme
      const cacheKey = `theme_${tenantInfo.id}`;
      const expiryTime = new Date().getTime() + (60 * 60 * 1000); // 1 hour
      localStorage.setItem(cacheKey, JSON.stringify(newTheme));
      localStorage.setItem(`${cacheKey}_expiry`, expiryTime.toString());
      
    } catch (err) {
      console.error('❌ Failed to set theme from tenant info:', err);
      Sentry.captureException(err, {
        tags: { source: 'theme', op: 'setThemeFromTenantInfo' },
        extra: { tenantId: tenantInfo?.id },
      });
      setError(err instanceof Error ? err.message : 'Failed to set theme');

      // Fallback to default theme
      setTheme(DEFAULT_THEME);
      applyCSSVariables(DEFAULT_THEME);
    }
  };

  useEffect(() => {
    if (tenantSlug) {
      // Load brand-specific theme for tenant
      loadTheme(tenantSlug);
    } else {
      // Apply default theme if no tenant slug
      const defaultTheme = getBrandTheme('zen-pacific-orlando'); // Default to Zen Pacific
      setTheme(defaultTheme);
      applyCSSVariables(defaultTheme);
    }
  }, [tenantSlug]);

  const value: ThemeContextType = {
    theme,
    tenantData,
    isLoading,
    error,
    loadTheme,
    setThemeFromTenantInfo
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};